import type {Node as ProseMirrorNode, ResolvedPos, Schema} from "@tiptap/pm/model";
import type {Selection, Transaction} from "@tiptap/pm/state";
import {TextSelection} from "@tiptap/pm/state";
import {
    clampTableColumns,
    clampTableRows,
    getEvenColumnWidths,
    insertColumnWidth,
    MAX_TABLE_COLUMNS,
    MAX_TABLE_ROWS,
    MIN_TABLE_COLUMNS,
    MIN_TABLE_ROWS,
    PRINTOUT_TABLE_CELL_NODE_NAME,
    PRINTOUT_TABLE_HEADER_NODE_NAME,
    PRINTOUT_TABLE_NODE_NAME,
    PRINTOUT_TABLE_ROW_NODE_NAME,
    removeColumnWidth,
    type PrintoutTableNodeAttrs,
} from "./printoutTableOptions";

// Column edits run from the final row backward so earlier positions remain valid.

export interface PrintoutTableLocation {
    cellIndex: number;
    node: ProseMirrorNode;
    pos: number;
    rowIndex: number;
}

export const getPrintoutTableColumnCount = (table: ProseMirrorNode): number => (
    Math.max(MIN_TABLE_COLUMNS, table.firstChild?.childCount ?? MIN_TABLE_COLUMNS)
);

export const getPrintoutTableRowCount = (table: ProseMirrorNode): number => (
    Math.max(MIN_TABLE_ROWS, table.childCount)
);

const getChildStart = (parent: ProseMirrorNode, parentPos: number, index: number): number => {
    let position = parentPos + 1;

    for (let childIndex = 0; childIndex < index && childIndex < parent.childCount; childIndex += 1) {
        position += parent.child(childIndex).nodeSize;
    }

    return position;
};

export const findPrintoutTable = (selection: Selection): PrintoutTableLocation | null => {
    const {$from} = selection;

    for (let depth = $from.depth; depth > 0; depth -= 1) {
        const node = $from.node(depth);

        if (node.type.name !== PRINTOUT_TABLE_NODE_NAME) continue;

        return {
            cellIndex: $from.depth > depth + 1 ? $from.index(depth + 1) : 0,
            node,
            pos: $from.before(depth),
            rowIndex: $from.index(depth),
        };
    }

    return null;
};

export const findPrintoutTableCellDepth = ($position: ResolvedPos): number | null => {
    for (let depth = $position.depth; depth > 0; depth -= 1) {
        const {name} = $position.node(depth).type;

        if (name === PRINTOUT_TABLE_CELL_NODE_NAME || name === PRINTOUT_TABLE_HEADER_NODE_NAME) {
            return depth;
        }
    }

    return null;
};

const createPrintoutTableCell = (
    schema: Schema,
    cellNodeName: string = PRINTOUT_TABLE_CELL_NODE_NAME,
): ProseMirrorNode | null => {
    const cellType = schema.nodes[cellNodeName];
    const paragraphType = schema.nodes.paragraph;

    if (!cellType || !paragraphType) return null;

    return cellType.createAndFill(null, paragraphType.create()) ?? cellType.create(null, paragraphType.create());
};

const createPrintoutTableRow = (
    schema: Schema,
    columns: number,
    cellNodeName: string = PRINTOUT_TABLE_CELL_NODE_NAME,
): ProseMirrorNode | null => {
    const rowType = schema.nodes[PRINTOUT_TABLE_ROW_NODE_NAME];

    if (!rowType) return null;

    const cells: ProseMirrorNode[] = [];

    for (let columnIndex = 0; columnIndex < columns; columnIndex += 1) {
        const cell = createPrintoutTableCell(schema, cellNodeName);

        if (!cell) return null;

        cells.push(cell);
    }

    return rowType.createChecked(null, cells);
};

export const createPrintoutTableNode = (
    schema: Schema,
    rows: number,
    cols: number,
    withHeaderRow = false,
    attrs: PrintoutTableNodeAttrs = {},
): ProseMirrorNode | null => {
    const tableType = schema.nodes[PRINTOUT_TABLE_NODE_NAME];

    if (!tableType) return null;

    const safeRows = clampTableRows(rows, 3);
    const safeCols = clampTableColumns(cols, 3);
    const headerCellNodeName = schema.nodes[PRINTOUT_TABLE_HEADER_NODE_NAME]
        ? PRINTOUT_TABLE_HEADER_NODE_NAME
        : PRINTOUT_TABLE_CELL_NODE_NAME;
    const rowNodes: ProseMirrorNode[] = [];

    for (let rowIndex = 0; rowIndex < safeRows; rowIndex += 1) {
        const row = createPrintoutTableRow(
            schema,
            safeCols,
            withHeaderRow && rowIndex === 0 ? headerCellNodeName : PRINTOUT_TABLE_CELL_NODE_NAME,
        );

        if (!row) return null;

        rowNodes.push(row);
    }

    return tableType.createChecked({
        ...attrs,
        colWidths: attrs.colWidths ?? getEvenColumnWidths(safeCols),
    }, rowNodes);
};

const setPrintoutTableColumnWidths = (
    tr: Transaction,
    location: PrintoutTableLocation,
    colWidths: number[],
) => {
    tr.setNodeMarkup(location.pos, undefined, {
        ...location.node.attrs,
        colWidths,
    });
};

const selectPrintoutTableCell = (
    tr: Transaction,
    tablePos: number,
    rowIndex: number,
    cellIndex: number,
) => {
    const table = tr.doc.nodeAt(tablePos);

    if (!table || table.type.name !== PRINTOUT_TABLE_NODE_NAME) return;

    const safeRowIndex = Math.min(Math.max(rowIndex, 0), table.childCount - 1);
    const row = table.child(safeRowIndex);
    const safeCellIndex = Math.min(Math.max(cellIndex, 0), row.childCount - 1);

    if (safeCellIndex < 0) return;

    const cellStart = getChildStart(row, getChildStart(table, tablePos, safeRowIndex), safeCellIndex);

    tr.setSelection(TextSelection.near(tr.doc.resolve(cellStart + 1), 1)).scrollIntoView();
};

export const canInsertPrintoutTableRow = (location: PrintoutTableLocation | null): boolean => (
    location !== null && location.node.childCount < MAX_TABLE_ROWS
);

export const canDeletePrintoutTableRow = (location: PrintoutTableLocation | null): boolean => (
    location !== null && location.node.childCount > MIN_TABLE_ROWS
);

export const canInsertPrintoutTableColumn = (location: PrintoutTableLocation | null): boolean => (
    location !== null && getPrintoutTableColumnCount(location.node) < MAX_TABLE_COLUMNS
);

export const canDeletePrintoutTableColumn = (location: PrintoutTableLocation | null): boolean => (
    location !== null && getPrintoutTableColumnCount(location.node) > MIN_TABLE_COLUMNS
);

export const insertPrintoutTableRow = (
    tr: Transaction,
    location: PrintoutTableLocation,
    rowIndex: number,
): boolean => {
    const row = createPrintoutTableRow(tr.doc.type.schema, getPrintoutTableColumnCount(location.node));

    if (!row) return false;

    const safeRowIndex = Math.min(Math.max(rowIndex, 0), location.node.childCount);

    tr.insert(getChildStart(location.node, location.pos, safeRowIndex), row);

    return true;
};

export const deletePrintoutTableRow = (
    tr: Transaction,
    location: PrintoutTableLocation,
): boolean => {
    if (!canDeletePrintoutTableRow(location)) return false;

    const rowStart = getChildStart(location.node, location.pos, location.rowIndex);

    tr.delete(rowStart, rowStart + location.node.child(location.rowIndex).nodeSize);
    selectPrintoutTableCell(tr, location.pos, location.rowIndex, location.cellIndex);

    return true;
};

export const insertPrintoutTableColumn = (
    tr: Transaction,
    location: PrintoutTableLocation,
    columnIndex: number,
): boolean => {
    const {node: table, pos} = location;
    const columns = getPrintoutTableColumnCount(table);

    for (let rowIndex = table.childCount - 1; rowIndex >= 0; rowIndex -= 1) {
        const row = table.child(rowIndex);
        const cell = createPrintoutTableCell(tr.doc.type.schema);

        if (!cell) return false;

        const insertIndex = Math.min(Math.max(columnIndex, 0), row.childCount);
        const rowStart = getChildStart(table, pos, rowIndex);

        tr.insert(getChildStart(row, rowStart, insertIndex), cell);
    }

    setPrintoutTableColumnWidths(tr, location, insertColumnWidth(
        (table.attrs as PrintoutTableNodeAttrs).colWidths,
        columns,
        columnIndex,
    ));

    return true;
};

export const deletePrintoutTableColumn = (
    tr: Transaction,
    location: PrintoutTableLocation,
): boolean => {
    if (!canDeletePrintoutTableColumn(location)) return false;

    const {cellIndex, node: table, pos} = location;
    const columns = getPrintoutTableColumnCount(table);

    for (let rowIndex = table.childCount - 1; rowIndex >= 0; rowIndex -= 1) {
        const row = table.child(rowIndex);

        // Ragged pasted rows keep at least one cell.
        if (row.childCount <= 1) continue;

        const deleteIndex = Math.min(Math.max(cellIndex, 0), row.childCount - 1);
        const cellStart = getChildStart(row, getChildStart(table, pos, rowIndex), deleteIndex);

        tr.delete(cellStart, cellStart + row.child(deleteIndex).nodeSize);
    }

    setPrintoutTableColumnWidths(tr, location, removeColumnWidth(
        (table.attrs as PrintoutTableNodeAttrs).colWidths,
        columns,
        cellIndex,
    ));
    selectPrintoutTableCell(tr, pos, location.rowIndex, cellIndex);

    return true;
};

export const findAdjacentPrintoutTableCell = (
    location: PrintoutTableLocation,
    direction: 1 | -1,
): {node: ProseMirrorNode; pos: number} | null => {
    const {cellIndex, node: table, pos, rowIndex} = location;
    let nextRowIndex = rowIndex;
    let nextCellIndex = cellIndex + direction;

    if (nextCellIndex < 0) {
        nextRowIndex -= 1;

        if (nextRowIndex < 0) return null;

        nextCellIndex = table.child(nextRowIndex).childCount - 1;
    } else if (nextCellIndex >= table.child(rowIndex).childCount) {
        nextRowIndex += 1;

        if (nextRowIndex >= table.childCount) return null;

        nextCellIndex = 0;
    }

    const nextRow = table.child(nextRowIndex);

    if (nextCellIndex < 0 || nextCellIndex >= nextRow.childCount) return null;

    return {
        node: nextRow.child(nextCellIndex),
        pos: getChildStart(nextRow, getChildStart(table, pos, nextRowIndex), nextCellIndex),
    };
};

export const selectPrintoutTableCellContent = (
    tr: Transaction,
    cell: {node: ProseMirrorNode; pos: number},
) => {
    tr.setSelection(TextSelection.between(
        tr.doc.resolve(cell.pos),
        tr.doc.resolve(cell.pos + cell.node.nodeSize),
    )).scrollIntoView();
};
